<template>
  <ModalDialog
    ref="dialog"
    :title="title"
    icon="fa-rocket"
    sizeClass="modal-xl"
    :submitting="submitting"
    :submitTitle="submitTitle"
    :submitDisabled="submitDisabled"
    :showSubmit="editing"
    :scrollable="!editing"
    @closed="onClosed"
    @submitted="onSubmitted">

    <FormGroup v-bind:form="autoComponentForm" v-if="!loading">
      <div v-if="hasComponentSelected">
        <ContentTitle>{{formatLookupName(componentData.autoComponentType.type, AutomationComponentTypeLookup)}}: {{componentData.autoComponentType.name}}</ContentTitle>
        <ContentSubTitle>{{componentData.autoComponentType.description}}</ContentSubTitle>

        <Scroller :height="500" hideHorizontal>
          <!-- Trigger -->
          <div v-if="isTriggerComponentType">

            <FormRow label="Trigger Watch Fields" v-if="triggerTypeHasWatchFields" helpTip="Fields available to watch for the trigger to control when the rule is triggered">
              <FormMultiSelect field="fieldUpdatedList" v-model="triggerFieldUpdatedList" :options="triggerTypeWatchFieldLookup" placeholder="Select Watch Fields" v-if="editing" />
              <FormStaticLabel v-if="!editing">{{formatMultiSelectLookupName(triggerFieldUpdatedList, triggerTypeWatchFieldLookup)}}</FormStaticLabel>
            </FormRow>

            <FormRow label="Output Objects" helpTip="Objects outputted by this trigger for other components to use" v-if="hasOutputObjects">
              <FormStaticLabel>
                <Tooltip :label="objectInfo.description" v-for="objectInfo of outputObjectList" :key="objectInfo.linkType">
                  <span class="label auto-comp-object-type output mr-2">{{formatComponentObjectLinkTypeName(objectInfo.linkType)}}</span>
                </Tooltip>
              </FormStaticLabel>
            </FormRow>

            <AutomationComponentField 
              v-for="(triggerFieldType, index) of componentData.autoComponentType.triggerSettingFieldList"
              :key="index"
              :editing="editing"
              :fieldTypeInfo="triggerFieldType"
              :componentFieldValueList="triggerFieldList"
              @update="updateField(triggerFieldList, triggerFieldType.field, $event)"
              />

          </div>

          <!-- Condition -->
          <div v-if="isConditionComponentType">

            <div class="row mb-2">
              <div class="col-8">
                <FormRow label="Required Objects" helpTip="Objects required by this condition to use" v-if="hasInputObjects">
                  <FormStaticLabel>
                    <Tooltip :label="objectInfo.description" v-for="objectInfo of inputObjectList" :key="objectInfo.linkType">
                      <span class="label auto-comp-object-type input mr-2">{{formatComponentObjectLinkTypeName(objectInfo.linkType)}}</span>
                    </Tooltip>
                  </FormStaticLabel>
                </FormRow>
              </div>
              <div class="col-4">
                <div class="text-right" v-if="editing && hasTextEntryConditionFields">
                  <FormButton @click="insertPlaceholderClicked" class="btn-sm btn-default ml-2"><i class="far fa-plus"></i> Insert Placeholder</FormButton>
                </div>
              </div>
            </div>

            <AutomationConditionField
              v-for="(fieldInstance, index) of conditionFieldList"
              :key="index"
              :value="fieldInstance"
              :isLastCondition="index == conditionFieldList.length-1"
              :ref="`condition-field-${index}`"
              @update="updateConditionField(index, $event)"
              @removeField="removeConditionField(index)"
              :fieldTypeList="componentData.autoComponentType.conditionFieldList"
              :editing="editing"
              @sourceFieldFocus="onConditionFieldFocused($event, index, true)"
              @valueFieldFocus="onConditionFieldFocused($event, index, false)"
            />

            <div v-if="editing">
              <FormButton class="btn-default btn-sm" @click="addConditionField"><i class="far fa-plus-circle"></i> Add Field</FormButton>
            </div>
            
          </div>

          <!-- Action -->
          <div v-if="isActionComponentType">
            <FormRow label="Required Objects" helpTip="Objects required by this action to use" v-if="hasInputObjects">
              <FormStaticLabel>
                <Tooltip :label="objectInfo.description" v-for="objectInfo of inputObjectList" :key="objectInfo.linkType">
                  <span class="label auto-comp-object-type input mr-2">{{formatComponentObjectLinkTypeName(objectInfo.linkType)}}</span>
                </Tooltip>
              </FormStaticLabel>
            </FormRow>

            <FormRow label="Output Objects" helpTip="Objects outputted by this action for other components to use" v-if="hasOutputObjects">
              <FormStaticLabel>
                <Tooltip :label="objectInfo.description" v-for="objectInfo of outputObjectList" :key="objectInfo.linkType">
                  <span class="label auto-comp-object-type output mr-2">{{formatComponentObjectLinkTypeName(objectInfo.linkType)}}</span>
                </Tooltip>
              </FormStaticLabel>
            </FormRow>

            <div class="text-right mb-2" v-if="editing && (hasMessageTemplateStyle || hasTextEntryActionFields)">
              <FormButton @click="copyMessageTemplateClicked" class="btn-sm btn-default" v-if="hasMessageTemplateStyle"><i class="far fa-copy"></i> Copy Message Template</FormButton>
              <FormButton @click="insertPlaceholderClicked" class="btn-sm btn-default ml-2" v-if="hasTextEntryActionFields"><i class="far fa-plus"></i> Insert Placeholder</FormButton>
            </div>

            <AutomationComponentField 
              v-for="(actionFieldType, index) of componentData.autoComponentType.actionFieldList"
              :key="index"
              :ref="`action-field-${actionFieldType.field}`"
              :editing="editing"
              :fieldTypeInfo="actionFieldType"
              :componentFieldValueList="actionFieldList"
              @update="updateField(actionFieldList, actionFieldType.field, $event)"
              @focus="onActionFieldFocused($event, actionFieldType.field)"
              />
          </div>

          <!-- Branch -->
          <div v-if="isBranchComponentType">
            <FormRow label="Required Objects" helpTip="Objects required by this branch to use" v-if="hasInputObjects">
              <FormStaticLabel>
                <Tooltip :label="objectInfo.description" v-for="objectInfo of inputObjectList" :key="objectInfo.linkType">
                  <span class="label auto-comp-object-type input mr-2">{{formatComponentObjectLinkTypeName(objectInfo.linkType)}}</span>
                </Tooltip>
              </FormStaticLabel>
            </FormRow>

            <FormRow label="Output Objects" helpTip="Objects outputted by this branch for other components to use" v-if="hasOutputObjects">
              <FormStaticLabel>
                <Tooltip :label="objectInfo.description" v-for="objectInfo of outputObjectList" :key="objectInfo.linkType">
                  <span class="label auto-comp-object-type output mr-2">{{formatComponentObjectLinkTypeName(objectInfo.linkType)}}</span>
                </Tooltip>
              </FormStaticLabel>
            </FormRow>
            <FormRow label="Branch Output Objects" helpTip="Objects outputted by this branch for child components to use" v-if="hasOutputChildObjects">
              <FormStaticLabel>
                <Tooltip :label="objectInfo.description" v-for="objectInfo of outputChildObjectList" :key="objectInfo.linkType">
                  <span class="label auto-comp-object-type output mr-2">{{formatComponentObjectLinkTypeName(objectInfo.linkType)}}</span>
                </Tooltip>
              </FormStaticLabel>
            </FormRow>

            <AutomationComponentField 
              v-for="(branchFieldType, index) of componentData.autoComponentType.branchFieldList"
              :key="index"
              :editing="editing"
              :fieldTypeInfo="branchFieldType"
              :componentFieldValueList="branchFieldList"
              @update="updateField(branchFieldList, branchFieldType.field, $event)"
              />
          </div>
        </Scroller>
      </div>

      <div v-if="isCreateMode && !hasComponentSelected">
        <ContentTitle>{{componentBrowserTitle}}</ContentTitle>
        <Scroller height="500">
          <AutomationComponentTypeBrowser :requiredTypes="requiredComponentTypes" @componentClicked="browserComponentTypeSelected" />
        </Scroller>
      </div>

      <AutomationInsertPlaceholderPopover ref="insertPlaceholderPopover" @insertClicked="insertPlaceholderInText">
      </AutomationInsertPlaceholderPopover>

      <AutomationCopyMessageTemplatePopover ref="copyMessageTemplatePopover" @copyClicked="copyMessageTemplateToFields">
      </AutomationCopyMessageTemplatePopover>

    </FormGroup>
  </ModalDialog>
</template>

<script>
import _ from 'lodash'
import form from '@/mixins/form'
import Utils from '@/utils/utils'

import ModalDialog from '@/components/modalDialog.vue'
import ModalDialogMixin from '@/mixins/modalDialog'
import Popover from '@/components/popover.vue'

import AutomationMixin from '@/modules/automations/mixins/automations'

import AutomationConditionField from '@/modules/automations/components/automationConditionField'
import AutomationComponentField from '@/modules/automations/components/automationComponentField'
import AutomationComponentTypeBrowser from '@/modules/automations/components/automationComponentTypeBrowser'
import AutomationInsertPlaceholderPopover from '@/modules/automations/components/automationInsertPlaceholderPopover'
import AutomationCopyMessageTemplatePopover from '@/modules/automations/components/automationCopyMessageTemplatePopover'

import { AutomationComponentTypes, AutomationComponentTypeLookup, AutomationFieldTypes } from '@/store/types'

export default {
  name: 'AutoRuleComponentDialog',
  mixins: [form, ModalDialogMixin, AutomationMixin],
  components: {
    ModalDialog,
    Popover,
    AutomationConditionField,
    AutomationComponentField,
    AutomationComponentTypeBrowser,
    AutomationInsertPlaceholderPopover,
    AutomationCopyMessageTemplatePopover
  },
  props: {
    isCreateMode: {
      type: Boolean,
      default: false
    },
    requiredComponentTypes: Array,
    editing: Boolean,
    autoRuleComponent: Object,
    availableModelList: Array,
    availablePlaceholderList: Array
  },
  computed: {
    title() {
      if(!this.editing) {
        if(!this.hasComponentSelected)
          return 'Component'
        return `${this.formatLookupName(this.componentData.autoComponentType.type,AutomationComponentTypeLookup)} - ${this.componentData.autoComponentType.name}`
      }

      return this.isCreateMode ? 'New Component' : 'Update Component'
    },
    submitTitle() {
      return this.isCreateMode ? 'Create' : 'Update'
    },
    submitDisabled() {
      if(this.isCreateMode) {
        if(!this.hasComponentSelected)
          return true
        
        // Component type info needs to be loaded
        if(!this.componentData.autoComponentType.id)
          return true
      }
      return false
    },
    hasComponentSelected() {
      if(this.componentData && this.componentData.autoComponentType)
        return true
      return false
    },
    isTriggerComponentType() {
      return this.componentData.autoComponentType.type == AutomationComponentTypes.trigger
    },
    isConditionComponentType() {
      return this.componentData.autoComponentType.type == AutomationComponentTypes.condition
    },
    isBranchComponentType() {
      return this.componentData.autoComponentType.type == AutomationComponentTypes.branch
    },
    isActionComponentType() {
      return this.componentData.autoComponentType.type == AutomationComponentTypes.action
    },

    // Triggers
    triggerTypeHasWatchFields() {
      return (this.componentData.autoComponentType.triggerWatchFieldList || []).length > 0
    },
    triggerTypeWatchFieldLookup() {
      return (this.componentData.autoComponentType.triggerWatchFieldList || []).map(watchField => {
        return {
          name: watchField.name,
          value: watchField.field
        }
      })
    },

    // Conditions
    conditionHasFields() {
      return this.conditionFieldList.length > 0
    },

    // Browser
    componentBrowserTitle() {
      if(this.requiredComponentTypes && this.requiredComponentTypes.length == 1)
        return `Select ${this.formatLookupName(this.requiredComponentTypes[0], AutomationComponentTypeLookup)}`

      return `Select Component`
    },
    hasOutputObjects() {
      return this.outputObjectList.length > 0
    },
    hasOutputChildObjects() {
      return this.outputChildObjectList.length > 0
    },
    hasInputObjects() {
      return this.inputObjectList.length > 0
    },
    hasOutputPlaceholders() {
      return this.outputPlaceholderList.length > 0
    },
    outputObjectList() {
      return this.componentData.autoComponentType.outputTypeList || []
    },
    outputChildObjectList() {
      return this.componentData.autoComponentType.outputChildTypeList || []
    },
    inputObjectList() {
      return this.componentData.autoComponentType.inputTypeList || []
    },
    outputPlaceholderList() {
      return this.componentData.autoComponentType.outputPlaceholderList || []
    },
    hasTextEntryActionFields() {
      if(this.isActionComponentType && this.componentData.autoComponentType.actionFieldList) {
        return this.componentData.autoComponentType.actionFieldList.some(fieldInfo => fieldInfo.fieldType ==  AutomationFieldTypes.text || fieldInfo.fieldType ==  AutomationFieldTypes.textArea)
      }
      return false
    },
    hasTextEntryConditionFields() {
      if(this.isConditionComponentType) {

        return this.conditionFieldList.some(fieldInfo => {
          let fieldType = this.componentData.autoComponentType.conditionFieldList.find(typeInfo => fieldInfo.field == typeInfo.field && fieldInfo.model == typeInfo.model)
          if(fieldType)
            return fieldType.fieldType == AutomationFieldTypes.text || fieldType.fieldType == AutomationFieldTypes.textArea
          return false
        })
      }
      return false

    },
    hasMessageTemplateStyle() {
      return (this.componentData.autoComponentType.typeData && this.componentData.autoComponentType.typeData.isMessageTemplateStyle)
    }
  },
  data: () => {
    return {
      loading: false,
      componentData: null,
      autoComponentForm: {},
      submitting: false,

      conditionFieldList: [],
      triggerFieldList: [],
      triggerFieldUpdatedList: [],
      actionFieldList: [],
      branchFieldList: [],

      lastActionFieldFocused: null,
      lastConditionFieldFocused: null
    };
  },
  created() {
    this.AutomationComponentTypeLookup = AutomationComponentTypeLookup
  },
  mounted() {
    // If not create mode, load the automation details
    if(!this.isCreateMode) {
      this.componentData = _.cloneDeep(this.autoRuleComponent || {})
      this.requestComponentType()
    }
  },
  methods: {
    show(options = {}) {
      // Reset form
      this.autoComponentForm = { }

      return ModalDialogMixin.methods.show.call(this, arguments)
    },
    requestComponentType() {
      this.$apiService.automationService.getComponentType(this.componentData.autoComponentTypeId)
      .then(response => {
        this.componentData.autoComponentType = response.data
        let compType = this.componentData.autoComponentType

        if(this.isTriggerComponentType) {
          this.triggerFieldList = this.componentData.triggerSettings.fieldList || []
          this.triggerFieldUpdatedList = this.componentData.triggerSettings.fieldUpdatedList.map(fieldUpdate => fieldUpdate.field)

          // Trigger Defaults
          if(this.isCreateMode) {
            let compType = this.componentData.autoComponentType
            compType.triggerSettingFieldList.forEach(triggerField => {
              this.triggerFieldList.push({
                field: triggerField.field,
                value: triggerField.defaultValue !== undefined ? triggerField.defaultValue : ''
              })
            })
          }          
        }

        if(this.isActionComponentType) {
          this.actionFieldList = this.componentData.actionSettings.fieldList || []

          // Action Defaults
          if(this.isCreateMode) {            
            compType.actionFieldList.forEach(actionField => {
              this.actionFieldList.push({
                field: actionField.field,
                value: actionField.defaultValue !== undefined ? actionField.defaultValue : ''
              })
            })
          }
        }

        if(this.isBranchComponentType) {
          this.branchFieldList = this.componentData.branchSettings.fieldList || []

          // Branch Defaults
          if(this.isCreateMode) {
            compType.branchFieldList.forEach(branchField => {
              this.branchFieldList.push({
                field: branchField.field,
                value: branchField.defaultValue !== undefined ? branchField.defaultValue : ''
              })
            })
          }
        }

        if(this.isConditionComponentType) {
          this.conditionFieldList = this.componentData.conditionSettings.fieldConditionList || []
        }
      })
      .catch(err => {
        console.error(err)
        this.displayNetworkError(err, 'Error loading component type')
      })

    },
    onClosed() {
      this.modalClosed(false)
    },
    onSubmitted() {
      // TODO: Any validation

      // Fill in details
      if(this.isTriggerComponentType) {
        this.componentData.triggerSettings.fieldList = this.triggerFieldList
        this.componentData.triggerSettings.fieldUpdatedList = this.triggerFieldUpdatedList.map(fieldName => {
          return {
            field: fieldName
          }
        })
      }
      if(this.isConditionComponentType) {
        this.componentData.conditionSettings.fieldConditionList = this.conditionFieldList
      }
      if(this.isActionComponentType) {
        this.componentData.actionSettings.fieldList = this.actionFieldList
      }
      if(this.isBranchComponentType) {
        this.componentData.branchSettings.fieldList = this.branchFieldList
      }

      // Default description
      // Server will generate the formatted description
      this.componentData.description = this.componentData.autoComponentType.description

      this.modalClosed(this.componentData)      
      this.hide()
    },
    addConditionField() {
      this.conditionFieldList.push({
        model: '',
        field: '',
        value: ''
      })
    },
    updateConditionField(index, fieldInfo) {
      this.$set(this.conditionFieldList, index, fieldInfo)
    },
    removeConditionField(index) {
      this.$delete(this.conditionFieldList, index)
    },
    updateField(fieldList, field, value) {
      var fieldItem = fieldList.find(fieldInfo => fieldInfo.field == field)
      if(fieldItem) {
        fieldItem.value = value
      } else {
        fieldList.push({
          field: field,
          value: value
        })
      }
    },

    // Browser
    browserComponentTypeSelected(componentTypeId) {

      // Setup the new component
      this.componentData = {
        description: '',
        sortOrder: 0,
        triggerSettings: {
          fieldList: [],
          fieldUpdatedList: []
        },
        conditionSettings: {
          fieldConditionList: []
        },
        branchSettings: {
          fieldList: [],
        },
        actionSettings: {
          fieldList: [],
        },
        autoComponentType: {},
        autoComponentTypeId: componentTypeId
      }


      this.requestComponentType()
    },
    insertPlaceholderClicked(evt) {
      this.$refs.insertPlaceholderPopover.displayFromElement(evt.target, this.availableModelList, this.availablePlaceholderList)
    },
    insertPlaceholderInText(code) {
      // Insert placeholder into last focused action field
      if(this.lastActionFieldFocused && this.isActionComponentType) {
        var actionFieldComponent = this.$refs[`action-field-${this.lastActionFieldFocused}`]
        if(_.isArray(actionFieldComponent))
          actionFieldComponent = actionFieldComponent[0]

        if(actionFieldComponent) {
          actionFieldComponent.insertTextAtCursor(`[${code}]`)
        }
      }
      if(this.lastConditionFieldFocused && this.isConditionComponentType) {
        var conditionFieldComponent = this.$refs[`condition-field-${this.lastConditionFieldFocused.index}`]
        if(_.isArray(conditionFieldComponent))
          conditionFieldComponent = conditionFieldComponent[0]

        if(conditionFieldComponent) {
          conditionFieldComponent.insertTextAtCursor(`[${code}]`, this.lastConditionFieldFocused.isSourceField)
        }
      }
    },
    onActionFieldFocused(evt, field) {
      this.lastActionFieldFocused = field
    },
    onConditionFieldFocused(evt, conditionFieldIndex, isSourceField) {
      this.lastConditionFieldFocused = {
        index: conditionFieldIndex,
        isSourceField: isSourceField
      }
    },
    copyMessageTemplateClicked(evt) {
      this.$refs.copyMessageTemplatePopover.displayFromElement(evt.target)
    },
    copyMessageTemplateToFields(messageTemplateData) {
      // Only support triggers/actions
      // Only actions would be using this
      // Assumes the components all use consistent field naming of subject/message for message style components
      if(this.isTriggerComponentType) {
        this.updateField(this.triggerFieldList, 'subject', messageTemplateData.templateSubject)
        this.updateField(this.triggerFieldList, 'message', messageTemplateData.templateContent)
      }
      if(this.isActionComponentType) {
        this.updateField(this.actionFieldList, 'subject', messageTemplateData.templateSubject)
        this.updateField(this.actionFieldList, 'message', messageTemplateData.templateContent)
      }

    }
  }
}

</script>